import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import selloraLogo from "@/assets/sellora-logo.png";
import { useAuth } from "@/hooks/useAuth";
import { useOrderNotifications } from "@/hooks/useOrderNotifications";
import { NavLink } from "@/components/NavLink";
import NotificationBell from "@/components/NotificationBell";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Package, LayoutDashboard, ShoppingCart, Wallet, Settings, LogOut, Store, Share2, Users, BarChart3, CreditCard, MessageSquare, Shield,
} from "lucide-react";

const fournisseurLinks = [
  { title: "Tableau de bord", url: "/fournisseur", icon: LayoutDashboard },
  { title: "Mes produits", url: "/fournisseur/produits", icon: Package },
  { title: "Commandes", url: "/fournisseur/commandes", icon: ShoppingCart },
  { title: "Wallet", url: "/fournisseur/wallet", icon: Wallet },
  { title: "Statistiques", url: "/fournisseur/statistiques", icon: BarChart3 },
  { title: "Paramètres", url: "/fournisseur/parametres", icon: Settings },
];

const dropshipperLinks = [
  { title: "Catalogue", url: "/dropshipper", icon: Store },
  { title: "Mes liens", url: "/dropshipper/liens", icon: Share2 },
  { title: "Mes ventes", url: "/dropshipper/ventes", icon: ShoppingCart },
  { title: "Wallet", url: "/dropshipper/wallet", icon: Wallet },
  { title: "Statistiques", url: "/dropshipper/statistiques", icon: BarChart3 },
  { title: "Paramètres", url: "/dropshipper/parametres", icon: Settings },
];

const adminLinks = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard },
  { title: "Utilisateurs", url: "/admin/utilisateurs", icon: Users },
  { title: "Produits", url: "/admin/produits", icon: Package },
  { title: "Commandes", url: "/admin/commandes", icon: ShoppingCart },
  { title: "Transactions", url: "/admin/transactions", icon: CreditCard },
  { title: "Retraits", url: "/admin/retraits", icon: Wallet },
  { title: "Statistiques", url: "/admin/statistiques", icon: BarChart3 },
  { title: "Messages", url: "/admin/messages", icon: MessageSquare },
  { title: "Audit", url: "/admin/audit", icon: Shield },
];

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const { role, profile, signOut } = useAuth();
  const navigate = useNavigate();
  useOrderNotifications();

  const links = role === "admin" ? adminLinks : role === "fournisseur" ? fournisseurLinks : dropshipperLinks;
  const roleLabel = role === "admin" ? "Admin" : role === "fournisseur" ? "Fournisseur" : "Dropshipper";

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <Sidebar className="border-r border-sidebar-border">
          <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
            <img src={selloraLogo} alt="SELLORA" className="h-8 w-8 rounded object-contain" />
            <span className="font-bold text-sidebar-foreground">SELLORA</span>
          </div>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs uppercase tracking-wider">{roleLabel}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {links.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink to={item.url} end className="hover:bg-sidebar-accent" activeClassName="bg-sidebar-accent text-sidebar-primary font-medium">
                          <item.icon className="mr-2 h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <div className="mt-auto border-t border-sidebar-border p-4">
            <div className="mb-3 text-sm text-sidebar-foreground">
              <p className="font-medium truncate">{profile?.full_name}</p>
              <p className="text-xs text-sidebar-foreground/60 truncate">{profile?.email}</p>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </Sidebar>

        <div className="flex flex-1 flex-col">
          <header className="flex h-14 items-center gap-4 border-b bg-card px-4 lg:px-6">
            <SidebarTrigger />
            <h2 className="text-lg font-semibold text-foreground">
              {roleLabel === "Admin" ? "Administration" : `Espace ${roleLabel}`}
            </h2>
            <div className="ml-auto">
              <NotificationBell />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default DashboardLayout;
